import { useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useOverlayLayer } from './overlayLayer';

export { useOverlayLayer } from './overlayLayer';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  /** The sheet's accessible name; also its visible heading unless `hideTitle`. */
  title: string;
  hideTitle?: boolean;
  children: ReactNode;
}

/**
 * A bottom sheet: the handheld form of a menu or a small dialog (ADR 0048 §3.3,
 * §6, §7.1).
 *
 * One scroller (`.sheet__scroll`), the document locked underneath, a scrim that
 * closes it. Rendered into `<body>` rather than in place, because a sheet opened
 * from inside a route must not inherit that route's stacking context or
 * overflow clipping — the same reason the calendar's detail overlay was
 * clipped on narrow widths.
 *
 * Height is the content's, capped at 85 % of the *visual* viewport when
 * `useVisualViewport()` is running (else `dvh`), so the top of a tall sheet
 * always leaves some page showing — which is what tells you it is a sheet you
 * can dismiss and not a new screen.
 */
export function Sheet({ open, onClose, title, hideTitle = false, children }: SheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  useOverlayLayer(panelRef, open, onClose);

  if (!open) return null;

  return createPortal(
    <div className="sheet">
      {/* A real button, as in the calendar's Dialog: the scrim is a way to
          dismiss the sheet, so it must be one for a screen reader too. */}
      <button
        type="button"
        className="sheet__scrim"
        aria-label={`Close ${title}`}
        tabIndex={-1}
        onClick={onClose}
      />
      <div
        ref={panelRef}
        className="sheet__panel"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <div className="sheet__handle" aria-hidden="true" />
        {!hideTitle && <h2 className="sheet__title">{title}</h2>}
        <div className="sheet__scroll">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
